import React from "react";
import { Box, Typography } from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import RoomIcon from "@mui/icons-material/Room";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { getFormattedDateByTimeZone } from "Core/data/Helpers";
import { getVidFleetEvent, getLightMetricEvent } from "Core/data/VideoEvents";
import { checkDeviceData } from "Core/data/Helpers";
import { setClipRequestData } from "Components/Map/components/ClipRequestDrawerMap/redux/slice";
import { useDispatch } from "react-redux";

/**
 * Extrae parámetros Config/Measured desde deviceData para eventos de cámara.
 * Soporta estructuras flexibles de VidFleet/LightMetrics.
 */
const extractEventParams = (deviceData) => {
  if (!deviceData) return [];
  const params = [];

  // Activation / Min Speed
  const activation = deviceData?.activation ?? deviceData?.minSpeed;
  if (activation) {
    const minSpeed = typeof activation === "object" ? activation?.minSpeed ?? activation?.value : activation;
    if (minSpeed)
      params.push({
        label: "Activation",
        subLabel: "Min Speed",
        config: minSpeed,
        measured: null,
      });
  }

  // Evaluated Value / G-Force
  const evaluatedValue = deviceData?.evaluatedValue ?? deviceData?.gForce ?? deviceData?.gForceValue;
  if (evaluatedValue) {
    const gForce = typeof evaluatedValue === "object" ? evaluatedValue : { config: evaluatedValue };
    params.push({
      label: "Evaluated Value",
      subLabel: "G-Force",
      config: gForce?.config ?? gForce?.configValue ?? null,
      measured: gForce?.measured ?? gForce?.measuredValue ?? null,
    });
  }

  // Pruning / Duration
  const pruning = deviceData?.pruning ?? deviceData?.duration;
  if (pruning) {
    const duration = typeof pruning === "object" ? pruning?.duration ?? pruning?.value : pruning;
    if (duration)
      params.push({
        label: "Pruning",
        subLabel: "Duration",
        config: duration,
        measured: null,
      });
  }

  return params;
};

const CLIP_REQUEST_DRAWER_BUTTON_ID = "clip-request-drawer-open-button";

export const TripEventInfoCard = ({
  data,
  messages,
  manufacturerName,
  closeInfoBubble,
}) => {
  const dispatch = useDispatch();
  const vt = data?.markerDetail?.vehicleTrail;
  const unit = data?.markerDetail?.unit;

  console.log('unit', unit);
  console.log('vt', vt);

  if (!vt) return null;

  const deviceData = checkDeviceData(vt?.deviceData);
  const isCameraEvent = vt?.eventCode?.includes?.("CameraEvent");
  const event = manufacturerName === "Mitac-Gemini" || manufacturerName === "LIGHTMETRICS"
    ? getLightMetricEvent(vt?.eventCode)
    : getVidFleetEvent(vt?.eventCode);
  const eventLabel = messages[event?.name] ?? vt?.eventName ?? "—";

  const severity = deviceData?.severity ?? deviceData?.level;
  const score = deviceData?.score ?? deviceData?.value;
  const eventHeaderParts = [eventLabel];
  if (severity) eventHeaderParts.push(severity);
  if (score != null) eventHeaderParts.push(`(${score})`);
  if (isCameraEvent) eventHeaderParts.push("Camera");
  const eventHeaderText = eventHeaderParts.join(" : ");

  const coords = vt?.latitude != null && vt?.longitude != null
    ? `${vt.latitude}, ${vt.longitude}`
    : "—";

  const formattedDate = getFormattedDateByTimeZone(
    vt?.unitTime,
    unit?.timeZone?.name,
    messages,
    "MM/DD/YYYY hh:mm:ss A",
    false
  ) || "—";

  const params = extractEventParams(deviceData);
  const hasParams = params.length > 0;

  const handleRequestVideo = () => {
    dispatch(
      setClipRequestData({
        event: {
          id: vt?.id,
          unitId: vt?.deviceId,
          lat: vt?.latitude,
          lng: vt?.longitude,
          unitTime: vt?.unitTime,
        },
      })
    );
    if (typeof closeInfoBubble === "function") {
      closeInfoBubble();
    }
    setTimeout(() => {
      const drawerButton = document.getElementById(CLIP_REQUEST_DRAWER_BUTTON_ID);
      if (drawerButton) {
        drawerButton.click();
      }
    }, 0);
  };

  return (
    <Box
      sx={{
        width: 360,
        minWidth: 320,
        bgcolor: "background.paper",
        borderRadius: 1,
        overflow: "hidden",
        boxShadow: 1,
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 1 }}>
          <NorthEastIcon sx={{ fontSize: 18, color: "text.secondary", mt: 0.25 }} />
          <Typography variant="body2" fontWeight={500}>
            {eventHeaderText}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
          <RoomIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            {coords}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ScheduleIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            {formattedDate}
          </Typography>
        </Box>
      </Box>

      {hasParams && (
        <>
          <Box sx={{ borderTop: 1, borderColor: "divider", mx: 2 }} />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                gap: 0,
                fontSize: "0.75rem",
              }}
            >
              <Box sx={{ fontWeight: 600, textAlign: "center", pb: 0.5, borderBottom: 1, borderColor: "divider" }} />
              <Box sx={{ fontWeight: 600, textAlign: "center", minWidth: 70, pb: 0.5, borderBottom: 1, borderColor: "divider" }}>
                Config
              </Box>
              <Box sx={{ fontWeight: 600, textAlign: "center", minWidth: 70, pb: 0.5, borderBottom: 1, borderColor: "divider" }}>
                Measured
              </Box>
              {params.map((p, i) => (
                <React.Fragment key={i}>
                  <Box sx={{ py: 0.75 }}>
                    <Typography variant="caption" component="div" fontWeight={500}>
                      {p.label}
                    </Typography>
                    <Typography variant="caption" component="div" color="text.secondary" sx={{ pl: 1 }}>
                      {p.subLabel}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 0.75 }}>
                    {p.config != null ? p.config : "—"}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 0.75 }}>
                    {p.measured != null ? p.measured : "—"}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </>
      )}

      <Box sx={{ px: 2, pb: 1.5, pt: hasParams ? 0 : 1, display: "flex", justifyContent: "flex-end" }}>
        <Typography
          component="button"
          variant="caption"
          onClick={handleRequestVideo}
          sx={{
            color: "primary.main",
            textDecoration: "underline",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
          }}
        >
          {messages["infoBubble_requestVideo"] ?? "Request Video"}
        </Typography>
      </Box>
    </Box>
  );
};
